import { useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Page } from '@/shared/ui/Page';
import { PageHeader } from '@/shared/ui/PageHeader';
import { BottomDock } from '@/shared/ui/BottomDock';
import { OperationRow } from '@/entities/ledger-operation/OperationRow';
import { ledgerApi } from '@/entities/ledger-operation/api';
import { subscriptionApi } from '@/entities/subscription/api';
import type { LedgerOperation, OperationType } from '@/entities/ledger-operation/types';
import { cn } from '@/shared/lib/cn';
import { useT } from '@/shared/lib/i18n';

type PaymentFilter = 'all' | 'payment' | 'charge' | 'correction';

function filterType(filter: PaymentFilter): OperationType | undefined {
  if (filter === 'payment') return 'payment';
  if (filter === 'charge') return 'charge';
  if (filter === 'correction') return 'correction';
  return undefined;
}

export function PaymentsPage() {
  const t = useT();
  const [filter, setFilter] = useState<PaymentFilter>('all');

  const holderQ = useQuery({
    queryKey: ['subscriptions', 'holder'],
    queryFn: () => subscriptionApi.list('holder'),
  });

  const subs = holderQ.data?.items.filter((sub) => sub.status === 'active' || sub.status === 'closing') ?? [];
  const operationQs = useQueries({
    queries: subs.map((sub) => ({
      queryKey: ['subscription-operations', sub.id, 'payments-page', filter],
      queryFn: () => ledgerApi.listOperations(sub.id, { type: filterType(filter), limit: 30 }),
      enabled: Boolean(sub.id),
      staleTime: 20_000,
    })),
  });

  const operations = useMemo(() => {
    const items: LedgerOperation[] = [];
    operationQs.forEach((query) => {
      if (query.data?.items) items.push(...query.data.items);
    });
    return items.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [operationQs]);

  const filters: { value: PaymentFilter; label: string }[] = [
    { value: 'all', label: t('payments.filters.all') },
    { value: 'payment', label: t('payments.filters.payments') },
    { value: 'charge', label: t('payments.filters.charges') },
    { value: 'correction', label: t('payments.filters.corrections') },
  ];

  return (
    <>
      <Page>
        <PageHeader title={<span className="font-bold text-[19px]">{t('pages.payments')}</span>} />
        <div className="px-[18px] pb-[96px] flex flex-col gap-3">
          <div className="flex gap-2 flex-wrap text-[12px] font-semibold">
            {filters.map((item) => (
              <button
                key={item.value}
                onClick={() => setFilter(item.value)}
                className={cn(
                  'rounded-full px-4 py-[10px] border transition-colors',
                  filter === item.value
                    ? 'text-white bg-brand-gradient border-transparent'
                    : 'bg-white/50 border-white/60 text-text-secondary',
                )}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="rounded-[18px] px-4 py-1 bg-[rgba(244,251,245,0.5)] border border-white/55">
            {operations.map((op) => <OperationRow key={op.id} op={op} />)}
            {!holderQ.isLoading && operations.length === 0 && (
              <p className="text-center text-[14px] text-text-secondary py-8">{t('payments.empty')}</p>
            )}
          </div>
        </div>
      </Page>
      <BottomDock />
    </>
  );
}
